/**
 * Buying Plus — the call site for RevenueCat's SDKs, one per till.
 *
 * Two lanes, chosen at the tap (avg-app-builder, 2026-09-09, accepted):
 *  - NATIVE: inside the Capacitor shell, `@revenuecat/purchases-capacitor` IS the
 *    bridge to StoreKit / Play Billing — an ordinary import, no window hook between
 *    us and the shell. Apple's rules make this the ONLY till on iOS.
 *  - WEB: in a browser, `@revenuecat/purchases-js` opens RevenueCat's Web Billing
 *    checkout (Stripe underneath).
 * Both imported DYNAMICALLY inside their branch: a browser user never downloads
 * bridge code, a shell user never downloads the web checkout.
 *
 * ALL THREE KEYS LIVE IN THE FRONTEND BUILD ENV (VITE_RC_IOS_KEY, VITE_RC_ANDROID_KEY,
 * VITE_RC_WEB_KEY), not in the shell: the shell loads this bundle from
 * app.avengents.ai, so a key baked into the .app would sit where the code that
 * needs it cannot read it — and rotating it would mean a trip through App Review.
 * They are PUBLIC SDK keys, the kind every store binary ships; the one secret in
 * this design (the webhook signing secret) never leaves the BFF.
 *
 * The app user id handed to RevenueCat is `/auth/me`'s `billing_id` — configured
 * once per sign-in, never anonymously (BILLING.md §2): an anonymous purchase would
 * belong to a device, and the webhook keys our plan rows by that id.
 */
import { fetchSession } from '../auth/googleSession'
import { ensureVoiceToken } from '../voice/voiceToken'
import { RC_PUBLIC_KEYS } from './keys'
import { isPaidPlan, refreshBilling, type Plan } from './plan'

export type Lane = 'native' | 'web' | 'none'
export type PurchaseOutcome = 'purchased' | 'cancelled' | 'failed' | 'unavailable'
/** What Plus costs on this platform, as the store states it: "$2.99" per "month". */
export type PlusOffer = { price: string; period: string }
/**
 * Why the price could not be read: the step that failed and the store's own words.
 * Shown VERBATIM on the paywall (owner, 2026-09-12: the TestFlight build sat on
 * "Asking the store for the price…" with Subscribe disabled and nobody could say why —
 * the sheet swallowed the reason and the promise had no deadline).
 */
export type PlusOfferFailure = { failure: string }
/**
 * How long one store request may take before the paywall says so and offers a retry.
 * LONGER than RevenueCat's own product-request timeout (30 s): at 20 s the sheet said
 * "no answer in 20 s" and nothing else, because the store logs WHY only when its own
 * clock runs out (TestFlight, 2026-09-12). Now the store gets to speak first.
 */
const OFFER_TIMEOUT_MS = 45_000

const ENTITLEMENT = 'plus'

/** The webhook race after a paid purchase: how long we wait for the plan row to flip
 *  (checkout.test.ts pins these two numbers as the deadline it exercises). */
const SETTLE_TRIES = 12
const SETTLE_GAP_MS = 800

type CapacitorGlobal = { getPlatform?: () => string; isNativePlatform?: () => boolean }

function capacitor(): CapacitorGlobal | null {
  const c = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor
  return c && typeof c === 'object' ? c : null
}

/** 'ios' | 'android' | 'web' — the shell's own answer, 'web' when there is no shell. */
export function platform(): string {
  try {
    return capacitor()?.getPlatform?.() ?? 'web'
  } catch {
    return 'web'
  }
}

/** The build env overrides; the committed public keys (keys.ts) are the defaults. */
function keyFor(plat: string): string {
  const env = import.meta.env as Record<string, string | undefined>
  if (plat === 'ios') return env['VITE_RC_IOS_KEY'] || RC_PUBLIC_KEYS.ios
  if (plat === 'android') return env['VITE_RC_ANDROID_KEY'] || RC_PUBLIC_KEYS.android
  return env['VITE_RC_WEB_KEY'] || RC_PUBLIC_KEYS.web
}

/** Which till this build can open right now. 'none' = no key for this platform. */
export function purchaseLane(): Lane {
  const plat = platform()
  if (!keyFor(plat)) return 'none'
  return plat === 'ios' || plat === 'android' ? 'native' : 'web'
}

/** The account's RevenueCat id AND email, from the one session — the web checkout
 *  pre-fills the receipt email with it (a Web Billing purchase emails a receipt). */
async function account(): Promise<{ id: string; email: string | null } | null> {
  const s = await fetchSession()
  return s.kind === 'in' && s.identity.billingId
    ? { id: s.identity.billingId, email: s.identity.email }
    : null
}

/**
 * The one package we sell, out of an offering: the monthly one, else the first.
 * A store's SDK returns a package only when it carries a product for THAT store,
 * so "no package" means the product is not set up for this till yet.
 */
function pickPackage<P>(offerings: { current: O<P> | null; all: Record<string, O<P>> }): P | null {
  const offering = offerings.current ?? offerings.all['default'] ?? null
  return offering?.monthly ?? offering?.availablePackages[0] ?? null
}
type O<P> = { monthly: P | null; availablePackages: P[] }

/** The web SDK, configured once per account — switched, never re-created, if it is up under another id. */
async function webSdk(apiKey: string, appUserId: string) {
  const { Purchases } = await import('@revenuecat/purchases-js')
  const p = Purchases.isConfigured() ? Purchases.getSharedInstance() : Purchases.configure({ apiKey, appUserId })
  if (p.getAppUserId() !== appUserId) await p.changeUser(appUserId)
  return p
}

/**
 * The store's own log lines — RevenueCat's warnings and errors, the last few. A
 * StoreKit request that never answers logs WHY it is waiting ("no products found", a
 * timeout, a configuration error), and nothing else on a phone can show that line to
 * anyone. A refusal on the paywall quotes them (owner, 2026-09-12: twenty seconds of
 * "Asking the store for the price…" and no way to say why).
 */
const storeLog: string[] = []
let storeLogArmed = false
async function armStoreLog(sdk: {
  setLogHandler?: (h: (...args: unknown[]) => void) => Promise<void>
  setLogLevel?: (o: { level: string }) => Promise<void>
}) {
  if (storeLogArmed) return
  storeLogArmed = true
  try {
    // DEBUG, and EVERY line kept (the last few): the request RevenueCat was making when
    // it stopped answering is the diagnosis, and it may be none of the ones a filter
    // would have guessed — on 2026-09-12 the App Store had handed the product over and
    // the sheet still waited, so the hang was somewhere else in the SDK's sequence.
    await sdk.setLogLevel?.({ level: 'DEBUG' })
    // The plugin hands the handler ONE object ({ logLevel, message }) at runtime, whatever
    // its type says — read as "(level, message)" the first prod build showed
    // "[object Object]: undefined" twice (2026-09-12). Both shapes are read.
    await sdk.setLogHandler?.((a, b) => {
      const o = a && typeof a === 'object' ? (a as { logLevel?: unknown; level?: unknown; message?: unknown }) : { logLevel: a, message: b }
      const text = `${String(o.logLevel ?? o.level ?? '')}: ${String(o.message ?? '').replace(/\s+/g, ' ').trim()}`
      storeLog.push(text.slice(0, 160))
      if (storeLog.length > 4) storeLog.shift()
    })
  } catch {
    // An older plugin without the handler: a refusal keeps only its own words.
  }
}
/** "· store said: …" — the last store log lines, or nothing when it said nothing. */
function storeSaid(): string {
  return storeLog.length ? ` · store said: ${storeLog.slice(-2).join(' | ')}` : ''
}

/** The Capacitor plugin's own type — the value `Purchases` the module exports. */
type NativeSdk = typeof import('@revenuecat/purchases-capacitor')['Purchases']

/**
 * The native plugin, configured once per account — logged in, never re-configured, if
 * it is up under another id.
 *
 * HANDED BACK INSIDE AN OBJECT, never as the value itself. Capacitor's plugin object is a
 * Proxy that answers EVERY property name with a method wrapper — `then` included — so to
 * `await` it, resolve a promise with it, or return it from an async function, it is a
 * thenable whose `then` never calls back, and the promise never settles. That was the
 * whole TestFlight hang (2026-09-12): 45 s of "waiting on getOfferings" while the bridge
 * log showed no getOfferings call at all — the JS sat on `return Purchases`. Calling
 * methods on it (`Purchases.getOfferings()`) is fine; those return real promises.
 */
async function nativeSdk(apiKey: string, appUserID: string): Promise<{ sdk: NativeSdk }> {
  at('import')
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  at('log handler')
  // Diagnostics must never be the thing that hangs: two seconds, then on without them.
  await Promise.race([
    armStoreLog(Purchases as unknown as Parameters<typeof armStoreLog>[0]),
    new Promise<void>((r) => setTimeout(r, 2_000)),
  ])
  at('isConfigured')
  const { isConfigured } = await Purchases.isConfigured()
  if (!isConfigured) {
    at('configure')
    await Purchases.configure({ apiKey, appUserID })
  } else {
    at('getAppUserID')
    if ((await Purchases.getAppUserID()).appUserID !== appUserID) {
      at('logIn')
      await Purchases.logIn({ appUserID })
    }
  }
  at('getOfferings')
  return { sdk: Purchases }
}

/** "P1M" → "month": the ISO period the stores hand back, as the word a sentence needs. */
function periodWord(iso: string | null | undefined): string {
  switch (iso) {
    case 'P1W':
      return 'week'
    case 'P1Y':
      return 'year'
    case 'P1M':
    default:
      return 'month'
  }
}

/**
 * What Plus costs on THIS platform, read from the store's offering and never typed
 * here (guideline 3.1.2: the price on the paywall is the price the store will charge,
 * in the buyer's own currency and format). null = no till on this platform, no
 * account, or no package for this store yet — the paywall then says so instead of
 * showing a number the store would not honour.
 */
/**
 * Which native call is in flight right now, so a deadline can say "waiting on
 * configure" rather than "no answer" (TestFlight, 2026-09-12: 45 s, the store said
 * nothing at all — so the request never even reached the store; which call sat there
 * is the diagnosis).
 */
let phase = ''
const at = (p: string) => {
  phase = p
}

/** One store step under a deadline: the step's name is what the failure will say. */
async function timed<T>(step: string, work: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`no answer in ${OFFER_TIMEOUT_MS / 1000} s${phase ? ` (waiting on ${phase})` : ''}`)),
      OFFER_TIMEOUT_MS,
    )
  })
  try {
    return await Promise.race([work, deadline])
  } catch (e) {
    throw new Error(`${step}: ${describeError(e)}`)
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/** The store's own words for an error — RevenueCat's code and underlying message when it has them. */
function describeError(e: unknown): string {
  const o = (e ?? {}) as { message?: unknown; code?: unknown; readableErrorCode?: unknown; underlyingErrorMessage?: unknown }
  const parts = [o.readableErrorCode ?? o.code, o.message, o.underlyingErrorMessage]
    .filter((x) => x !== undefined && x !== null && String(x).trim() !== '')
    .map((x) => String(x).trim())
  return (parts.length ? parts.join(' · ') : String(e)).slice(0, 200)
}

/**
 * null = nothing to sell here (no key, signed out, or an offering with no package);
 * a failure = the store was asked and did not answer, with the reason — never swallowed.
 */
export async function plusOffer(): Promise<PlusOffer | PlusOfferFailure | null> {
  const lane = purchaseLane()
  if (lane === 'none') return null
  try {
    at('')
    const acct = await timed('session', account())
    if (!acct) return null
    const key = keyFor(platform())
    if (lane === 'native') {
      const offerings = await timed(
        'store',
        (async () => (await nativeSdk(key, acct.id)).sdk.getOfferings())(),
      )
      const pkg = pickPackage(offerings)
      if (pkg) return { price: pkg.product.priceString, period: periodWord(pkg.product.subscriptionPeriod) }
      // An offering with no package: null when the store said nothing (nothing to sell
      // here), a named failure when it did — that line is the whole diagnosis.
      return storeLog.length ? { failure: `store: the offering has no package for this platform${storeSaid()}` } : null
    }
    at('')
    const offerings = await timed('store', (async () => (await webSdk(key, acct.id)).getOfferings())())
    const pkg = pickPackage(offerings)
    if (!pkg) return null
    const product = pkg.webBillingProduct
    return { price: product.currentPrice.formattedPrice, period: periodWord(product.normalPeriodDuration) }
  } catch (e) {
    return { failure: `${e instanceof Error ? e.message : String(e)}${storeSaid()}` }
  }
}

async function viaWeb(appUserId: string, apiKey: string, email: string | null): Promise<PurchaseOutcome> {
  const { ErrorCode, PurchasesError } = await import('@revenuecat/purchases-js')
  const p = await webSdk(apiKey, appUserId)
  const pkg = pickPackage(await p.getOfferings())
  if (!pkg) return 'unavailable'
  try {
    // No htmlTarget → RevenueCat renders its own checkout modal (Stripe card entry
    // inside it); on success it returns customerInfo directly, so the UI flips
    // without waiting for the webhook (the webhook still lands, to set the plan the
    // voice token reads). customerEmail pre-fills the receipt field.
    const r = await p.purchase({ rcPackage: pkg, ...(email ? { customerEmail: email } : {}) })
    return ENTITLEMENT in r.customerInfo.entitlements.active ? 'purchased' : 'failed'
  } catch (e) {
    if (e instanceof PurchasesError && e.errorCode === ErrorCode.UserCancelledError) return 'cancelled'
    return 'failed'
  }
}

async function viaNative(appUserID: string, apiKey: string): Promise<PurchaseOutcome> {
  const { PURCHASES_ERROR_CODE } = await import('@revenuecat/purchases-capacitor')
  const { sdk: Purchases } = await nativeSdk(apiKey, appUserID)
  const pkg = pickPackage(await Purchases.getOfferings())
  if (!pkg) return 'unavailable'
  try {
    const r = await Purchases.purchasePackage({ aPackage: pkg })
    return ENTITLEMENT in r.customerInfo.entitlements.active ? 'purchased' : 'failed'
  } catch (e) {
    const err = e as { code?: unknown; userCancelled?: unknown }
    if (err?.userCancelled === true || String(err?.code) === String(PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR)) {
      return 'cancelled'
    }
    return 'failed'
  }
}

/**
 * The till has closed with a purchase: the store has the money and RevenueCat has
 * the entitlement — but the BFF learns of it from RevenueCat's WEBHOOK, which races
 * this very code. The web checkout resolves the instant the card clears and the
 * webhook takes a second or two, so ONE read of `/account/plan` right now is a coin
 * flip — and the voice token is minted FROM that row, so a token minted before the
 * flip carries the free `daily_s`, and the next clip of a person who just paid is
 * metered against the wrong allowance.
 *
 * So: re-read until the row says Plus (bounded — a dozen tries, under ten seconds),
 * THEN re-mint the token, then read the meter under it. Not a heartbeat: it starts
 * only when a purchase completes, stops the moment the row agrees, and gives up at
 * the deadline (the row then shows whatever the BFF says; the next menu open re-reads).
 */
async function settlePurchase(): Promise<void> {
  for (let i = 0; i < SETTLE_TRIES; i++) {
    // Any paid tier ends the settle poll — a buyer who is already Ultra must not
    // spin here waiting for the word 'plus'.
    if (isPaidPlan((await refreshBilling()).plan?.plan)) break
    await new Promise<void>((r) => setTimeout(r, SETTLE_GAP_MS))
  }
  await ensureVoiceToken(true)
  await refreshBilling()
}

/**
 * Open the till for Plus and, when it closes with a purchase, make the app act on
 * it: wait for the plan row to reflect it, then re-mint the voice token so the next
 * clip carries the new `daily_s` — the contract's "re-fetch plan + re-mint token".
 */
export async function buyPlus(): Promise<PurchaseOutcome> {
  const lane = purchaseLane()
  if (lane === 'none') return 'unavailable'
  const acct = await account()
  if (!acct) return 'failed'
  const key = keyFor(platform())
  let outcome: PurchaseOutcome
  try {
    outcome = lane === 'native' ? await viaNative(acct.id, key) : await viaWeb(acct.id, key, acct.email)
  } catch {
    outcome = 'failed'
  }
  if (outcome === 'purchased') await settlePurchase()
  return outcome
}

/** Where a subscriber changes or ends the plan: the store that sold it. */
function manageUrl(plan: Plan): string | null {
  switch (plan.store) {
    case 'app_store':
      return 'https://apps.apple.com/account/subscriptions'
    case 'play_store':
      return 'https://play.google.com/store/account/subscriptions'
    case 'web':
      return null // RevenueCat's customer portal — asked of the SDK in manageSubscription
    default:
      return null
  }
}

/** RevenueCat's customer portal for a web subscription — asked of the SDK, null when it has none. */
async function portalUrl(): Promise<string | null> {
  const key = keyFor('web')
  const acct = await account()
  if (!key || !acct) return null
  try {
    return (await (await webSdk(key, acct.id)).getCustomerInfo()).managementURL
  } catch {
    return null
  }
}

/**
 * Open the right place to manage the subscription; false when there is none to open.
 *
 * The store URLs are known at once and open straight from the tap. The web portal's
 * URL takes a session read and a RevenueCat call to learn, and a window opened AFTER
 * an await is no longer "the user clicked" to a browser — it is a popup, and it is
 * blocked without a word (the owner's "Manage 無效", 2026-09-10, with the portal URL
 * sitting right there in the SDK's answer). So for the web the tab is opened NOW, in
 * the click, and pointed at the portal once the URL is known — closed again if there
 * is none. A browser that refuses even that gets the portal in this tab instead.
 */
export async function manageSubscription(plan: Plan): Promise<boolean> {
  const direct = manageUrl(plan)
  if (direct) {
    window.open(direct, '_blank', 'noopener')
    return true
  }
  if (plan.store !== 'web') return false
  const tab = window.open('', '_blank')
  if (tab) tab.opener = null
  const url = await portalUrl()
  if (!url) {
    tab?.close()
    return false
  }
  if (tab) tab.location.href = url
  else window.location.assign(url)
  return true
}
